import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { isLoggedIn } from '../../utils/auth';

// Get the base URL and auth header
const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4611/auth';

export const faqApi = createApi({
  reducerPath: 'faqApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}`,
    prepareHeaders: (headers) => {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (token && isLoggedIn()) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['FaqCategory', 'Faq'],
  endpoints: (builder) => ({
    // FAQ Category endpoints
    getFaqCategories: builder.query({
      query: () => '/faq/categories',
      providesTags: ['FaqCategory'],
    }),
    getFaqCategory: builder.query({
      query: (categoryId) => `/faq/categories/${categoryId}`,
      providesTags: (result, error, categoryId) => [{ type: 'FaqCategory', id: categoryId }],
    }),
    createFaqCategory: builder.mutation({
      query: (category) => ({
        url: '/faq/categories',
        method: 'POST',
        body: category,
      }),
      invalidatesTags: ['FaqCategory'],
    }),
    updateFaqCategory: builder.mutation({
      query: ({ categoryId, ...category }) => ({
        url: `/faq/categories/${categoryId}`,
        method: 'PUT',
        body: category,
      }),
      invalidatesTags: (result, error, { categoryId }) => [
        { type: 'FaqCategory', id: categoryId },
        'FaqCategory',
      ],
    }),
    deleteFaqCategory: builder.mutation({
      query: (categoryId) => ({
        url: `/faq/categories/${categoryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FaqCategory'],
    }),

    // FAQ item endpoints
    getFaq: builder.query({
      query: (categoryId) => categoryId ? `/faq/items/${categoryId}` : '/faq/items',
      providesTags: (result, error, categoryId) => [
        { type: 'Faq', id: categoryId || 'all' },
        'Faq',
      ],
      transformResponse: (response) => {
        // Add a transformResponse to handle different possible response structures
        if (Array.isArray(response)) {
          return response;
        } else if (response.data && Array.isArray(response.data)) {
          return response.data;
        } else if (response.items && Array.isArray(response.items)) {
          return response.items;
        } else {
          console.error('Unexpected faqItems structure:', response);
          return [];
        }
      },
    }),
    createFaq: builder.mutation({
      query: (faq) => ({
        url: '/faq/items',
        method: 'POST',
        body: faq,
      }),
      invalidatesTags: (result, error, faq) => [
        { type: 'Faq', id: faq.category_id },
        'Faq',
      ],
    }),
    updateFaq: builder.mutation({
      query: ({ faqId, ...faq }) => ({
        url: `/faq/items/${faqId}`,
        method: 'PUT',
        body: faq,
      }),
      invalidatesTags: (result, error, { faqId, category_id }) => [
        { type: 'Faq', id: category_id },
        'Faq',
      ],
    }),
    deleteFaq: builder.mutation({
      query: (faqId) => ({
        url: `/faq/items/${faqId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Faq'],
    }),

    // Add this new endpoint
    reloadRag: builder.mutation({
      query: () => ({
        url: '/faq/reload-rag',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetFaqCategoriesQuery,
  useGetFaqCategoryQuery,
  useCreateFaqCategoryMutation,
  useUpdateFaqCategoryMutation,
  useDeleteFaqCategoryMutation,
  useGetFaqQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useReloadRagMutation,
} = faqApi; 